import { lazy, Suspense, useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  CalendarClock,
  CircleAlert,
  ClipboardList,
  Database,
  FileText,
  FolderOpen,
  Hourglass,
  PackageX,
  PieChart,
  Repeat,
  ShieldCheck,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import {
  CLASSIFICATION_LABEL,
  Classification,
  RNC_SEVERITY_LABEL,
  RNC_STATUS_LABEL,
  THERMOMETER_LABEL,
  UserRole,
  type ActionCounterDto,
  type ActionKey,
  type DashboardDto,
  type DistributionSliceDto,
  type EvaluationDto,
  type NonconformityDto,
  type ParetoRowDto,
  type ThermometerDto,
  type ThermometerLevel,
} from '@weloze/shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  formatCompetence,
  formatDate,
  formatDateTime,
  formatDecimal,
  formatIndex,
} from '../lib/format';
import {
  Alert,
  Badge,
  buttonVariants,
  Card,
  cn,
  EmptyState,
  Skeleton,
} from '../components/ui';

/** Sob demanda: o Recharts só é baixado por quem chega a ver a série. */
const IqwChart = lazy(() => import('../components/IqwChart'));

/**
 * A página inicial.
 *
 * Duas telas na mesma rota, porque as perguntas são diferentes: o fornecedor
 * quer saber **como foi a minha última nota e por quê**; a Weloze quer saber
 * **o que precisa da minha ação hoje**.
 *
 * Antes daqui esta tela era um placeholder da fatia (a) com quatro cartões
 * escritos à mão dizendo "Não aplicável" — o que passou a mentir no dia em que
 * passou a existir nota.
 */
export function DashboardPage() {
  const { user } = useAuth();
  if (!user) return null;

  const isWeloze = user.role === UserRole.WELOZE_ADMIN || user.role === UserRole.WELOZE_VIEWER;
  return isWeloze ? <WelozeHome name={user.name} /> : <SupplierHome />;
}

const TONE: Record<Classification, 'success' | 'warning' | 'danger' | 'neutral'> = {
  EXCELLENT: 'success',
  GOOD: 'warning',
  UNSATISFACTORY: 'danger',
  NOT_APPLICABLE: 'neutral',
};

// ---------------------------------------------------------------------------
// Weloze — o que precisa de ação
// ---------------------------------------------------------------------------

/**
 * O painel da Qualidade (PLAN §9, fatia j).
 *
 * A ordem da tela é a ordem da pergunta: **o que precisa de mim hoje** vem
 * antes de **como está a carteira**. Um painel que abre com gráfico responde a
 * uma pergunta que ninguém fez ao ligar o computador de manhã.
 *
 * Bloco de carteira sem conteúdo **não é desenhado**. Não é economia de tela: é
 * o que faz o painel encolher quando está tudo calmo e crescer quando há o que
 * olhar — em vez de exibir oito cartões vazios que ensinam a ignorar a tela.
 *
 * E não há IQW médio em destaque, de propósito: com 17 avaliadas de 47 ele
 * parece a nota da base de fornecedores, mas é a nota de quem entregou naquele
 * mês. A distribuição conta a mesma coisa sem a armadilha, e a série de doze
 * meses mostra a inclinação, que é a leitura que serve para alguma coisa.
 */
function WelozeHome({ name }: { name: string }) {
  const [data, setData] = useState<DashboardDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setData(await api.get<DashboardDto>('/dashboard'));
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Não foi possível carregar o painel.');
      }
    })();
  }, []);

  if (error) return <Alert>{error}</Alert>;

  if (!data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-16 w-96" />
        <Skeleton className="h-28" />
        <Skeleton className="h-24" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
          Olá, {name.split(' ')[0]}
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          {data.period ? (
            <>
              Última competência publicada:{' '}
              <strong className="text-slate-900">{data.period.label}</strong>.
            </>
          ) : (
            'Nenhuma competência publicada ainda.'
          )}
        </p>
      </div>

      <Thermometer data={data.thermometer} />
      <Actions actions={data.actions} />
      <Portfolio data={data} />
      <ImportHealth run={data.lastImport} />
    </div>
  );
}

// --- Termômetro -------------------------------------------------------------

const THERMOMETER_STYLE: Record<ThermometerLevel, { card: string; dot: string; text: string }> = {
  GREEN: { card: 'border-emerald-200 bg-emerald-50/50', dot: 'bg-emerald-500', text: 'text-emerald-800' },
  YELLOW: { card: 'border-amber-200 bg-amber-50/50', dot: 'bg-amber-500', text: 'text-amber-800' },
  RED: { card: 'border-red-200 bg-red-50/50', dot: 'bg-red-500', text: 'text-red-800' },
};

/**
 * O termômetro, com a régua ao lado.
 *
 * A cor sozinha não ensina nada: quem vê amarelo precisa saber o que falta para
 * o verde e o que levaria ao vermelho. É o mesmo princípio da escada do ISQ no
 * relatório do fornecedor — um indicador que não diz como sair de onde está não
 * muda comportamento nenhum.
 */
function Thermometer({ data }: { data: ThermometerDto }) {
  const estilo = data.level ? THERMOMETER_STYLE[data.level] : null;

  return (
    <Card className={estilo ? estilo.card : ''}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1 basis-64">
          <div className="flex items-center gap-2">
            <span
              className={cn('size-3 shrink-0 rounded-full', estilo ? estilo.dot : 'bg-slate-300')}
              aria-hidden
            />
            <h2 className={cn('font-medium', estilo ? estilo.text : 'text-slate-600')}>
              {data.level ? THERMOMETER_LABEL[data.level] : 'Sem medição'}
            </h2>
          </div>
          <p className="mt-1 text-sm text-slate-700">{data.reason}</p>

          {data.criticalUnsatisfactory.length > 0 && (
            <ul className="mt-2 flex flex-wrap gap-2">
              {data.criticalUnsatisfactory.map((loja) => (
                <li key={loja.storeId}>
                  <Link
                    to={`/admin/fornecedores/${loja.storeId}`}
                    className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800 underline-offset-4 hover:underline"
                  >
                    {loja.supplierName}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        {data.evaluated > 0 && (
          <dl className="flex shrink-0 gap-4 text-center">
            {(
              [
                ['Excelente', data.excellent, 'text-emerald-700'],
                ['Bom', data.good, 'text-amber-700'],
                ['Insatisfatório', data.unsatisfactory, 'text-red-700'],
              ] as const
            ).map(([rotulo, valor, cor]) => (
              <div key={rotulo}>
                <dd className={cn('text-2xl font-semibold tabular-nums', cor)}>{valor}</dd>
                <dt className="text-xs text-slate-500">{rotulo}</dt>
              </div>
            ))}
          </dl>
        )}
      </div>

      <p className="mt-3 border-t border-slate-200/70 pt-3 text-xs text-slate-500">
        <strong className="font-medium text-slate-600">A régua:</strong> verde exige nenhuma
        insatisfatória e ao menos {fmtPercent(data.requiredExcellentPercent)} em Excelente; vermelho
        começa em {fmtPercent(data.actionPercent)} de insatisfatórias — ou em uma só, se ela fornecer
        item com característica de segurança.
      </p>
    </Card>
  );
}

// --- Ação -------------------------------------------------------------------

const ACTION_ICON: Record<ActionKey, typeof CalendarClock> = {
  PERIOD_TO_CLOSE: CalendarClock,
  RNC_OVERDUE: CircleAlert,
  CERTIFICATIONS_PENDING: ShieldCheck,
  ACTION_PLANS_OVERDUE: ClipboardList,
  STORES_WITH_PENDENCIES: FolderOpen,
};

/**
 * Os contadores clicáveis.
 *
 * Zero continua aparecendo, e apagado. Sumir seria pior: quem não vê o cartão
 * não sabe se está tudo em dia ou se o painel esqueceu de perguntar.
 */
function Actions({ actions }: { actions: ActionCounterDto[] }) {
  return (
    <section>
      <h2 className="text-sm font-medium uppercase tracking-wide text-slate-500">
        O que precisa de ação
      </h2>
      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {actions.map((acao) => {
          const Icone = ACTION_ICON[acao.key];
          const vazio = acao.count === 0;
          return (
            <Link
              key={acao.key}
              to={acao.to}
              title={acao.hint}
              className={cn(
                'rounded-xl border bg-white p-4 shadow-sm transition-colors',
                vazio
                  ? 'border-slate-200 hover:border-slate-300'
                  : 'border-brand-200 hover:border-brand-400',
              )}
            >
              <div className="flex items-center gap-2">
                <Icone
                  className={cn('size-4', vazio ? 'text-slate-300' : 'text-brand-600')}
                  aria-hidden
                />
                <span
                  className={cn(
                    'text-3xl font-semibold tabular-nums',
                    vazio ? 'text-slate-300' : 'text-slate-900',
                  )}
                >
                  {acao.count}
                </span>
              </div>
              <p className="mt-1 text-sm font-medium text-slate-800">{acao.label}</p>
              <p className="mt-0.5 text-xs text-slate-500">{acao.hint}</p>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

// --- Carteira ---------------------------------------------------------------

function Portfolio({ data }: { data: DashboardDto }) {
  const temSerie = data.trend.some((ponto) => ponto.average !== null);
  const temDistribuicao = data.distribution.some((fatia) => fatia.count > 0);

  if (!temDistribuicao && !temSerie && data.pareto.length === 0) {
    return (
      <EmptyState
        title="Nenhuma competência publicada ainda"
        description="Feche e publique um mês em Competências para a carteira aparecer aqui."
      />
    );
  }

  return (
    <section>
      <h2 className="text-sm font-medium uppercase tracking-wide text-slate-500">A carteira</h2>

      <div className="mt-3 grid gap-4 xl:grid-cols-2">
        {temDistribuicao && (
          <Block title="Distribuição" icon={<PieChart className="size-4" aria-hidden />}>
            <Distribution slices={data.distribution} period={data.period} />
          </Block>
        )}

        {temSerie && (
          <Block
            title="IQW médio, 12 meses"
            icon={<TrendingUp className="size-4" aria-hidden />}
            hint="A inclinação é a leitura; o valor de um mês isolado é a nota de quem entregou naquele mês."
          >
            <Suspense fallback={<Skeleton className="h-64" />}>
              <IqwChart
                dados={data.trend.map((ponto) => ({
                  competencia: formatCompetence(ponto.competence).slice(0, 3),
                  rotulo: formatCompetence(ponto.competence),
                  iqw: ponto.average,
                }))}
              />
            </Suspense>
          </Block>
        )}

        {data.drops.length > 0 && (
          <Block
            title="Maiores quedas"
            icon={<TrendingDown className="size-4" aria-hidden />}
            hint="Quem foi de 95 para 84 continua “Bom” e não aparece em lista nenhuma — mas é agora que a conversa custa barato."
          >
            <ul className="divide-y divide-slate-100">
              {data.drops.map((queda) => (
                <li
                  key={queda.storeId}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm"
                >
                  <Link
                    to={`/admin/fornecedores/${queda.storeId}`}
                    className={NOME_DA_LOJA}
                  >
                    {queda.supplierName}
                  </Link>
                  <span className="tabular-nums text-slate-500">
                    {formatIndex(queda.previous)} → {formatIndex(queda.current)}
                  </span>
                  <Badge tone="danger">{formatDelta(queda.delta)} pontos</Badge>
                </li>
              ))}
            </ul>
          </Block>
        )}

        {data.recurrence.length > 0 && (
          <Block
            title="Reincidência"
            icon={<Repeat className="size-4" aria-hidden />}
            hint="RNC em meses seguidos. Volume é evento; sequência é processo que não foi corrigido."
          >
            <ul className="divide-y divide-slate-100">
              {data.recurrence.map((linha) => (
                <li key={linha.storeId} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm">
                  <Link
                    to={`/admin/fornecedores/${linha.storeId}`}
                    className={NOME_DA_LOJA}
                  >
                    {linha.supplierName}
                  </Link>
                  <span className="text-xs text-slate-500">
                    {linha.competences.map((c) => formatCompetence(c).slice(0, 3)).join(' → ')}
                  </span>
                  <Badge tone={linha.competences.length >= 3 ? 'danger' : 'warning'}>
                    {linha.competences.length} meses seguidos
                  </Badge>
                </li>
              ))}
            </ul>
          </Block>
        )}

        {data.pareto.length > 0 && (
          <Block
            title="Onde as RNCs se concentram"
            icon={<BarChart3 className="size-4" aria-hidden />}
            hint="Por fornecedor, e não por causa: o formulário não tem campo de categoria de defeito, e agrupar por texto livre daria um gráfico bonito e falso."
          >
            <Pareto rows={data.pareto} />
          </Block>
        )}

        {data.aging.some((faixa) => faixa.count > 0) && (
          <Block
            title="Idade das RNCs abertas"
            icon={<Hourglass className="size-4" aria-hidden />}
            hint="Uma de cem dias em aberto não é a mesma coisa que dez de uma semana — e as duas somam igual em qualquer contador."
          >
            <ul className="space-y-1.5">
              {data.aging.map((faixa) => (
                <li key={faixa.label} className="flex flex-wrap items-center gap-3 text-sm">
                  <span className="w-28 shrink-0 text-slate-600 sm:w-32">{faixa.label}</span>
                  <Bar value={faixa.count} total={maxOf(data.aging.map((f) => f.count))} />
                  <span className="w-6 shrink-0 text-right tabular-nums text-slate-800">
                    {faixa.count}
                  </span>
                  {faixa.overdue > 0 && (
                    <Badge tone="danger">{faixa.overdue} com prazo vencido</Badge>
                  )}
                </li>
              ))}
            </ul>
          </Block>
        )}

        <Block
          title="Cobertura de certificação"
          icon={<ShieldCheck className="size-4" aria-hidden />}
          hint="A faixa sai do mesmo cálculo do ISQ. Quem não tem certificado nenhum entra como “sem certificação” — senão a cobertura sairia melhor do que é."
        >
          <ul className="space-y-1.5">
            {data.certificationCoverage.map((faixa) => (
              <li key={faixa.tier} className="flex items-center gap-3 text-sm">
                <span className="min-w-0 flex-1 truncate text-slate-600">{faixa.label}</span>
                <span className="text-xs text-slate-400">
                  {faixa.demeritPercent === 0 ? 'sem demérito' : `−${faixa.demeritPercent}% no ISQ`}
                </span>
                <span className="w-6 shrink-0 text-right tabular-nums text-slate-800">
                  {faixa.count}
                </span>
              </li>
            ))}
          </ul>
        </Block>

        {data.staleSuppliers.length > 0 && (
          <Block
            title="Monitoradas sem compra"
            icon={<PackageX className="size-4" aria-hidden />}
            hint="Não é falha do fornecedor: é a Weloze que não comprou. Continuam custando ficha, certificado e acompanhamento."
          >
            <ul className="divide-y divide-slate-100">
              {data.staleSuppliers.map((loja) => (
                <li
                  key={loja.storeId}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm"
                >
                  <Link
                    to={`/admin/fornecedores/${loja.storeId}`}
                    className={NOME_DA_LOJA}
                  >
                    {loja.supplierName}
                  </Link>
                  <span className="text-xs text-slate-500">
                    {loja.lastCompetence
                      ? `última em ${formatCompetence(loja.lastCompetence)}`
                      : 'nenhum item encerrado até hoje'}
                  </span>
                </li>
              ))}
            </ul>
          </Block>
        )}
      </div>
    </section>
  );
}

function Block({
  title,
  icon,
  hint,
  children,
}: {
  title: string;
  icon: ReactNode;
  hint?: string;
  children: ReactNode;
}) {
  return (
    // `min-w-0` porque item de grid nasce com `min-width: auto` e se recusa a
    // encolher abaixo do conteúdo. Como a trilha é uma só no celular, o cartão
    // mais teimoso alargava todos os outros e empurrava a página para fora da
    // tela — medido a 375 px: 407 de largura numa janela de 375.
    <Card className="min-w-0">
      <h3 className="flex items-center gap-2 font-medium text-slate-900">
        <span className="text-slate-400">{icon}</span>
        {title}
      </h3>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
      <div className="mt-3">{children}</div>
    </Card>
  );
}

function Distribution({
  slices,
  period,
}: {
  slices: DistributionSliceDto[];
  period: DashboardDto['period'];
}) {
  const total = slices.reduce((soma, fatia) => soma + fatia.count, 0);

  return (
    <div>
      <p className="text-xs text-slate-500">
        {total} {total === 1 ? 'loja avaliada' : 'lojas avaliadas'}
        {period && ` em ${period.label}`}.
      </p>
      <ul className="mt-2 space-y-1.5">
        {slices.map((fatia) => (
          <li key={fatia.classification} className="flex items-center gap-3 text-sm">
            <span className="w-28 shrink-0 text-slate-600">
              {CLASSIFICATION_LABEL[fatia.classification]}
            </span>
            <Bar value={fatia.count} total={total} tone={TONE[fatia.classification]} />
            <span className="w-6 shrink-0 text-right tabular-nums text-slate-800">
              {fatia.count}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Pareto({ rows }: { rows: ParetoRowDto[] }) {
  const maior = maxOf(rows.map((linha) => linha.count));

  return (
    <ul className="space-y-2">
      {rows.map((linha) => (
        <li key={linha.storeId} className="text-sm">
          <div className="flex items-center gap-3">
            <Link
              to={`/admin/fornecedores/${linha.storeId}`}
              className="w-28 shrink-0 truncate text-slate-800 underline-offset-4 hover:underline sm:w-40"
            >
              {linha.supplierName}
            </Link>
            <Bar value={linha.count} total={maior} tone="danger" />
            <span className="w-6 shrink-0 text-right tabular-nums text-slate-800">
              {linha.count}
            </span>
            <span className="w-14 shrink-0 text-right text-xs tabular-nums text-slate-400">
              {formatIndex(linha.cumulativePercent)}
            </span>
          </div>
          {/* A contagem sozinha mente: três Menores e uma Maior somam quatro
              dos dois lados, e custam 60% e 50% do IQA. */}
          <p className="ml-28 pl-3 text-xs text-slate-500 sm:ml-40">
            {(['MAJOR', 'MEDIUM', 'MINOR'] as const)
              .filter((gravidade) => linha.bySeverity[gravidade] > 0)
              .map((gravidade) => `${linha.bySeverity[gravidade]} ${RNC_SEVERITY_LABEL[gravidade]}`)
              .join(' · ')}
          </p>
        </li>
      ))}
    </ul>
  );
}

/**
 * O nome do fornecedor nas listas da carteira.
 *
 * `basis-full` no celular: com `flex-1` sozinho o nome encolhia até virar
 * “PI…” ao lado dos números, em vez de a linha quebrar. Quem lê precisa saber
 * de quem é a queda antes de saber o tamanho dela.
 */
const NOME_DA_LOJA =
  'min-w-0 basis-full truncate text-slate-800 underline-offset-4 hover:underline sm:flex-1 sm:basis-0';

const BAR_TONE = {
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  danger: 'bg-red-500',
  neutral: 'bg-slate-300',
} as const;

function Bar({
  value,
  total,
  tone = 'neutral',
}: {
  value: number;
  total: number;
  tone?: keyof typeof BAR_TONE;
}) {
  const largura = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-slate-100">
      <div className={cn('h-full rounded-full', BAR_TONE[tone])} style={{ width: `${largura}%` }} />
    </div>
  );
}

/**
 * A saúde da última importação, discreta.
 *
 * Fica no rodapé e sem cor quando deu certo: importação que funcionou não é
 * notícia. Falha vira alerta, porque aí os números do mês estão parados no
 * tempo e ninguém percebe olhando um índice.
 */
function ImportHealth({ run }: { run: DashboardDto['lastImport'] }) {
  if (!run) return null;

  const falhou = run.status === 'FAILED';
  return (
    <section>
      <p
        className={cn(
          'flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border px-3 py-2 text-xs',
          falhou
            ? 'border-red-200 bg-red-50/60 text-red-800'
            : 'border-slate-200 bg-slate-50/60 text-slate-500',
        )}
      >
        <Database className="size-3.5 shrink-0" aria-hidden />
        {falhou ? (
          <>
            <strong className="font-medium">A última importação do Protheus falhou</strong>
            {run.errorMessage && <span>— {run.errorMessage}</span>}
          </>
        ) : (
          <>
            Protheus sincronizado em {formatDateTime(run.startedAt)} · {run.itemsClosed} itens
            encerrados · {run.suppliersUpdated} cadastros atualizados
          </>
        )}
        <Link to="/admin/importacao" className="underline-offset-4 hover:underline">
          ver importações
        </Link>
      </p>
    </section>
  );
}

function maxOf(values: number[]): number {
  return values.reduce((maior, valor) => (valor > maior ? valor : maior), 0);
}

function fmtPercent(value: number): string {
  return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;
}

/** Queda do IQW com o sinal de menos tipográfico, como o resto do sistema. */
function formatDelta(value: number): string {
  return `\u2212${formatDecimal(Math.abs(value))}`;
}

// ---------------------------------------------------------------------------
// Fornecedor — a própria nota, e por quê
// ---------------------------------------------------------------------------

function SupplierHome() {
  const { user, currentStore } = useAuth();
  const [serie, setSerie] = useState<EvaluationDto[] | null>(null);
  const [rncs, setRncs] = useState<NonconformityDto[]>([]);
  const [error, setError] = useState<string | null>(null);

  const storeId = currentStore?.storeId;

  const load = useCallback(async () => {
    if (!storeId) return;
    setError(null);
    try {
      const [avaliacoes, naoConformidades] = await Promise.all([
        api.get<EvaluationDto[]>(`/stores/${storeId}/evaluations`),
        api.get<NonconformityDto[]>(`/stores/${storeId}/nonconformities`),
      ]);
      setSerie(avaliacoes);
      setRncs(naoConformidades);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível carregar suas avaliações.');
    }
  }, [storeId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!user) return null;

  if (user.stores.length === 0) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
          Olá, {user.name.split(' ')[0]}
        </h1>
        <Card>
          <h2 className="font-medium text-slate-900">Acesso ainda sem loja vinculada</h2>
          <p className="mt-2 text-sm text-slate-600">
            Seu usuário existe, mas nenhuma loja foi liberada. Fale com a Qualidade da Weloze pelo
            e-mail{' '}
            <a
              href="mailto:qualidade@weloze.com.br"
              className="font-medium text-brand-600 underline-offset-2 hover:underline"
            >
              qualidade@weloze.com.br
            </a>
            .
          </p>
        </Card>
      </div>
    );
  }

  // A série vem em ordem crescente; a última é a competência mais recente.
  const ultima = serie?.at(-1) ?? null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
          Olá, {user.name.split(' ')[0]}
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          {currentStore?.companyName} · {currentStore?.protheusRef}
        </p>
      </div>

      {error && <Alert>{error}</Alert>}

      {/* As RNCs vêm antes da nota: é o que **exige ação** do fornecedor, e a
          nota é o retrato do que já passou. */}
      <SupplierRncs rncs={rncs} />

      {!serie ? (
        <div className="space-y-3">
          <Skeleton className="h-28" />
          <Skeleton className="h-64" />
        </div>
      ) : serie.length === 0 ? (
        <EmptyState
          title="Nenhuma avaliação publicada ainda"
          description="Assim que a Qualidade da Weloze publicar a primeira competência, ela aparece aqui com o detalhamento de cada índice."
        />
      ) : (
        <>
          {ultima && <SupplierScore evaluation={ultima} storeId={storeId!} />}
          <SeriesChart serie={serie} />
        </>
      )}
    </div>
  );
}

/**
 * As RNCs do fornecedor, com o caminho para o 8D.
 *
 * Sem esta lista o fornecedor **não tem como chegar ao formulário**: o link do
 * e-mail ainda não existe (fatia k), e sem porta de entrada o 8D no portal seria
 * uma tela que ninguém abre.
 *
 * As abertas primeiro, e as encerradas recolhidas: quem entra aqui está atrás do
 * que falta fazer, não do histórico.
 */
function SupplierRncs({ rncs }: { rncs: NonconformityDto[] }) {
  const abertas = rncs.filter((rnc) => rnc.status !== 'CLOSED' && rnc.status !== 'CANCELLED');
  if (rncs.length === 0) return null;

  return (
    <Card>
      <h2 className="font-medium text-slate-900">
        {abertas.length === 0
          ? 'Nenhuma RNC em tratativa'
          : abertas.length === 1
            ? '1 RNC aguardando o seu 8D'
            : `${abertas.length} RNCs aguardando o seu 8D`}
      </h2>
      <p className="mt-1 text-sm text-slate-600">
        O tratamento é feito aqui no portal: contenção, causa raiz, ações e evidências.
      </p>

      <ul className="mt-3 divide-y divide-slate-100">
        {(abertas.length > 0 ? abertas : rncs.slice(0, 5)).map((rnc) => {
          const atrasada = rnc.stages.some((etapa) => etapa.overdue);
          return (
            <li key={rnc.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm">
              <Link
                to={`/rncs/${rnc.id}`}
                className="rounded bg-slate-100 px-1.5 py-0.5 font-mono font-medium text-slate-800 underline-offset-4 hover:bg-slate-200 hover:underline"
              >
                {rnc.number}
              </Link>
              <span className="min-w-0 flex-1 truncate text-slate-700">{rnc.description}</span>
              <span className="text-xs text-slate-500">{formatDate(rnc.issuedAt)}</span>
              {atrasada && <Badge tone="danger">etapa atrasada</Badge>}
              {(rnc.status === 'CLOSED' || rnc.status === 'CANCELLED') && (
                <Badge tone="neutral">{RNC_STATUS_LABEL[rnc.status]}</Badge>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function SupplierScore({ evaluation, storeId }: { evaluation: EvaluationDto; storeId: string }) {
  const [year, month] = evaluation.competence.split('-');
  const cartoes = [
    { sigla: 'IDC', valor: evaluation.idc, o_que: 'cumprimento de prazo' },
    { sigla: 'IQA', valor: evaluation.iqa, o_que: 'qualidade assegurada' },
    { sigla: 'ISQ', valor: evaluation.isq, o_que: 'sistema da qualidade' },
    { sigla: 'IQW', valor: evaluation.iqw, o_que: 'índice geral' },
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-600">
          Competência{' '}
          <strong className="text-slate-900">{formatCompetence(evaluation.competence)}</strong>
          <Badge tone={TONE[evaluation.classification]} className="ml-2">
            {CLASSIFICATION_LABEL[evaluation.classification]}
          </Badge>
        </p>
        <Link
          to={`/relatorio/${storeId}/${year}/${Number(month)}`}
          className={buttonVariants({ variant: 'outline', size: 'sm' })}
        >
          <FileText className="size-4" aria-hidden />
          Ver o relatório completo
        </Link>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {cartoes.map((cartao) => (
          <Card key={cartao.sigla} className={cartao.sigla === 'IQW' ? 'border-brand-200' : ''}>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
              {cartao.sigla}
            </p>
            <p className="mt-2 text-3xl font-semibold tabular-nums text-slate-900">
              {formatIndex(cartao.valor)}
            </p>
            <p className="mt-1 text-xs text-slate-400">{cartao.o_que}</p>
          </Card>
        ))}
      </div>
    </div>
  );
}

/**
 * A evolução do IQW.
 *
 * Mês sem avaliação vira `null`, e não zero — o Recharts corta a linha ali, que
 * é a leitura correta: não houve nota, e não uma nota ruim. A faixa Excelente e
 * a faixa Bom entram como linhas de referência para a posição ter significado
 * sem precisar decorar os números.
 */
function SeriesChart({ serie }: { serie: EvaluationDto[] }) {
  const dados = serie.slice(-12).map((item) => ({
    competencia: formatCompetence(item.competence).slice(0, 3),
    rotulo: formatCompetence(item.competence),
    iqw: item.iqw,
  }));

  const temNota = dados.some((d) => d.iqw !== null);

  return (
    <Card>
      <h2 className="flex items-center gap-2 font-medium text-slate-900">
        <TrendingUp className="size-4 text-slate-400" aria-hidden />
        Evolução do IQW
      </h2>
      <p className="mt-1 text-sm text-slate-600">
        Últimas {dados.length} competências publicadas. Mês sem avaliação não aparece na linha.
      </p>

      {!temNota ? (
        <p className="mt-4 text-sm text-slate-500">
          Ainda não há competência com nota para desenhar a série.
        </p>
      ) : (
        <div className="mt-4">
          <Suspense fallback={<Skeleton className="h-64" />}>
            <IqwChart dados={dados} />
          </Suspense>
        </div>
      )}
    </Card>
  );
}
